import { DecimalPipe, registerLocaleData } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import { ErrorHandler, NgModule, CUSTOM_ELEMENTS_SCHEMA, provideZonelessChangeDetection } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { EuropeanDateAdapter, EUROPEAN_DATE_FORMATS } from './utils/european-date-adapter';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgSelectModule } from '@ng-select/ng-select';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { createCacheBustingTranslateLoader } from './utils/cache-busting-translate-loader';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { SortableModule } from 'ngx-bootstrap/sortable';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { CookieService } from 'ngx-cookie-service';
import { ngxLoadingAnimationTypes, NgxLoadingModule } from 'ngx-loading';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { TreeviewConfig } from 'src/app/bo/treeview/treeview-config';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { CarteComponent } from './components/carte/carte.component';
import { ConfigDonneesAnalyseModaleComponent } from './components/config-donnees-analyse-modale/config-donnees-analyse-modale.component';
import { ConfigDonneesAnalyseComponent } from './components/config-donnees-analyse/config-donnees-analyse.component';
import { ConnexionComponent } from './components/connexion/connexion.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CreerMotDePasseComponent } from './components/creer-mot-de-passe/creer-mot-de-passe.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { DonneesAnalyseModaleComponent } from './components/donnees-analyse-modale/donnees-analyse-modale.component';
import { BlastKpiComponent } from './features/blast/blast-kpi/blast-kpi.component';
import { DonneesSuiviOperationnelModaleComponent } from './components/donnees-suivi-operationnel-modale/donnees-suivi-operationnel-modale.component';
import { DonneesSuiviOperationnelComponent } from './components/donnees-suivi-operationnel/donnees-suivi-operationnel.component';
import { DonneesVibrationModaleComponent } from './components/donnees-vibration-modale/donnees-vibration-modale.component';
import { DonneesVibrationComponent } from './components/donnees-vibration/donnees-vibration.component';
import { FilArianeComponent } from './components/fil-ariane/fil-ariane.component';
import { FileExplorerComponent } from './components/file-explorer/file-explorer.component';
import { MesProjetsComponent } from './components/mes-projets/mes-projets.component';
import { MesTirsComponent } from './components/mes-tirs/mes-tirs.component';
import { ModaleChangerLangueComponent } from './components/modale-changer-langue/modale-changer-langue.component';
import { ModaleChangerMotPasseComponent } from './components/modale-changer-mot-passe/modale-changer-mot-passe.component';
import { ModaleResultatComponent } from './components/modale-resultat/modale-resultat.component';
import { MonProjetComponent } from './components/mon-projet/mon-projet.component';
import { MonSiteComponent } from './components/mon-site/mon-site.component';
import { NavBarLeftComponent } from './components/nav-bar-left/nav-bar-left.component';
import { NavBarTopComponent } from './components/nav-bar-top/nav-bar-top.component';
import { NavBarComponent } from './layout/nav-bar/nav-bar.component';
import { BugReportComponent } from './shared/components/bug-report/bug-report.component';
import { OperateursModaleComponent } from './components/operateurs-modale/operateurs-modale.component';
import { OperateursComponent } from './components/operateurs/operateurs.component';
import { OperationModaleComponent } from './components/operation-modale/operation-modale.component';
import { ProjetDetailComponent } from './components/projet-detail/projet-detail.component';
import { ProjetModaleComponent } from './components/projet-modale/projet-modale.component';
import { SiteReportsModaleComponent } from './components/site-reports-modale/site-reports-modale.component';
import { RapportCreationModaleComponent } from './components/rapport-creation-modale/rapport-creation-modale.component';
import { ReinitMotDePasseComponent } from './components/reinit-mot-de-passe/reinit-mot-de-passe.component';
import { SelectionDureeComponent } from './components/selection-duree/selection-duree.component';
import { SiteCarteComponent } from './components/site-carte/site-carte.component';
import { SiteControlPointsModaleComponent } from './components/site-control-points-modale/site-control-points-modale.component';
import { SiteControlPointsComponent } from './components/site-control-points/site-control-points.component';
import { SiteModaleComponent } from './components/site-modale/site-modale.component';
import { SiteSetupComponent } from './components/site-setup/site-setup.component';
import { OperationsComponent } from './components/operations/operations.component';
import { SiteTemplateSheetConfigComponent } from './components/site-template-sheet-config/site-template-sheet-config.component';
import { SiteUsersComponent } from './components/site-setup/site-users/site-users.component';
import { SiteComponent } from './components/site/site.component';
import { TirDetailComponent } from './components/tir-detail/tir-detail.component';
import { TirModaleComponent } from './components/tir-modale/tir-modale.component';
import { TreeviewItemComponent } from './components/treeview-item/treeview-item.component';
import { TreeviewComponent } from './components/treeview/treeview.component';
import { ZoneModaleComponent } from './components/zone-modale/zone-modale.component';
import { ZoneComponent } from './components/zone/zone.component';
import { MyMissingTranslationHandler } from './modules/MyMissingTranslationHandler';
import { DefaultTreeviewEventParser, TreeviewEventParser } from './modules/treeview-event-parser';
import { FormatTransducerTypePipe } from './pipes/format-transducer-type.pipe';
import { LocalizedDatePipe } from './pipes/localized-date.pipe';
import { SiteOrthophotosModaleComponent } from './components/site-setup/site-orthophotos-modale/site-orthophotos-modale.component';
import { SiteKpiComponent } from './components/site-kpi/site-kpi.component';
import { EpsgModaleComponent } from './components/epsg-modale/epsg-modale.component';
import { NoeudDetailComponent } from './components/noeud-detail/noeud-detail.component';
import { AddUsersToSiteModaleComponent } from './components/add-users-to-site-modale/add-users-to-site-modale.component';
import { SismographControlPointComponent } from './components/sismograph-control-point/sismograph-control-point.component';
import { GraphQLModule } from './graphql.module';
import { NgxFileDropModule } from 'ngx-file-drop';
import { SismographControlPointModalComponent } from './components/sismograph-control-point-modal/sismograph-control-point-modal.component';
import { ColorPickerModule } from 'ngx-color-picker';
import { SecurityWindowComponent } from './components/security-window/security-window.component';

import { SiteOrthophotosAddFilesModalComponent } from './components/site-setup/site-orthophotos-add-files-modal/site-orthophotos-add-files-modal.component';
import { SiteDxfAddFileModalComponent } from './components/site-setup/site-dxf-add-file-modal/site-dxf-add-file-modal.component';

import { RockTypeGuiComponent } from './components/site-setup/rock-type-gui/rock-type-gui.component';
import { RockTypeModalComponent } from './components/site-setup/rock-type-modal/rock-type-modal.component';

import { AccueilComponent } from './components/keycloak/acceuil/acceuil.component';

import { ProfileComponent } from './components/keycloak/profile/profile.component';
import { AreaComponent } from './features/area/area.component';
import { provideKeycloak, withAutoRefreshToken, AutoRefreshTokenService, UserActivityService, INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG, includeBearerTokenInterceptor, createInterceptorCondition, IncludeBearerTokenCondition } from 'keycloak-angular';
import { provideHttpClient, withInterceptorsFromDi, withInterceptors } from '@angular/common/http';
import { PosthogErrorHandler } from './services/posthog-error-handler';
import { environment } from 'src/environments/environment';

import { HibooDashboardComponent } from './components/hiboo-dashboard/hiboo-dashboard.component';
import { EquipmentsComponent } from './components/equipments/equipments.component';
import { EquipmentsModaleComponent } from './components/equipments-modale/equipments-modale.component';
import { DrillPauseReasonModalComponent } from './components/site-setup/drill-pause-reason-modal/drill-pause-reason-modal.component';
import { HibooDashboardBlastComponent } from './components/hiboo-dashboard-blast/hiboo-dashboard-blast.component';

import { HoleChargeViewComponent } from './components/holecharge-view/holecharge-view.component';
// Products modal components moved to ProductsModule

import { ImportModalComponent } from './components/import-modal/import-modal.component';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { DeliveryModaleComponent } from './components/deliveries/delivery-modale/delivery-modale.component';
import { DxfManagementComponent } from './components/site-setup/dxf-management/dxf-management.component';
import { PreviewDialogComponent } from './components/site-setup/crusher-data-import/preview-dialog.component';
import { Blast3DViewerLazyComponent } from './components/blast3-dviewer-lazy/blast3-dviewer-lazy.component';
import { ModifyKpiModalComponent } from './components/modify-kpi-modal/modify-kpi-modal.component';
import { ModifyLoadComponent } from './components/deliveries/modify-load/modify-load.component';
import { ModifyHoleComponent } from './components/deliveries/modify-hole/modify-hole.component';
import { VibrationDetailPage } from './components/vibration/vibration-detail/vibration-detail.page';
import { VibrationChartComponent } from './components/vibration/vibration-chart/vibration-chart.component';
import { RecordDetailComponent } from './components/vibration/record-detail/record-detail.component';
import { RecordsAssignmentComponent } from './components/vibration/records-assignment/records-assignment.component';
import { AutocompleteComponent } from './components/auto-complete/autocomplete.component';
import { RecordModalComponent } from './components/vibration/record-modal/record-modal.component';
import { AssignRecordsModalComponent } from './components/vibration/assign-records-modal/assign-records-modal.component';
import { CostComponent } from './components/cost/cost.component';
import { TirInfoComponent } from './components/mon-projet/tir-info/tir-info.component';
import { UserDashboardComponent } from './components/user-dashboard/user-dashboard.component';

import { CacheBustingInterceptor } from './utils/cache-busting.interceptor';
import { GeneralComponent } from './features/blast/blast-files/general.component';
import { BlastHolesComponent } from './features/blast/blast-holes/blast-holes.component';
import { FragmentationComponent } from './features/blast/fragmentation/fragmentation.component';
import { BlastEnvironmentComponent } from './features/blast/blast-environment/blast-environment.component';
import { BlastVibrationComponent } from './features/blast/blast-vibration/blast-vibration.component';
import { SharedModule } from './shared/shared.module';
import { ButtonComponent } from '@libs/front/components/button/button.component';
// Standalone components
import { CustomerFeedbackComponent } from './features/blast/customer-feedback/customer-feedback.component';
import { SupplierFeedbackComponent } from './features/blast/supplier-feedback/supplier-feedback.component';
// GeologyGuiComponent and WorkflowModule imported by RegionSetupComponent (standalone)
import { NavLeftComponent } from './layout/nav-left/nav-left.component';
import { PageBarComponent } from './layout/page-bar/page-bar.component';

// Utilisé par le LocalizedDatePipe
registerLocaleData(localeFr);
registerLocaleData(localeEs);

export function createTranslateLoader(http: HttpClient) {
    return createCacheBustingTranslateLoader(http);
}

export function getBaseUrl() {
  return document.getElementsByTagName('base')[0].href;
}


@NgModule({
    declarations: [
        AppComponent,
        SiteModaleComponent,
        CreerMotDePasseComponent,
        ConnexionComponent,
        ModaleResultatComponent,
        NavBarTopComponent,
        NavBarLeftComponent,
        ModaleChangerLangueComponent,
        ModaleChangerMotPasseComponent,
        ReinitMotDePasseComponent,
        SiteComponent,
        ZoneComponent,
        ZoneModaleComponent,
        MonSiteComponent,
        MesProjetsComponent,
        ProjetModaleComponent,
        SiteReportsModaleComponent,
        MonProjetComponent,
        TirDetailComponent,
        ProjetDetailComponent,
        FilArianeComponent,
        TirModaleComponent,
        MesTirsComponent,
        ConfigDonneesAnalyseComponent,
        ConfigDonneesAnalyseModaleComponent,
        FileExplorerComponent,
        DonneesAnalyseModaleComponent,
        DonneesSuiviOperationnelComponent,
        DonneesSuiviOperationnelModaleComponent,
        SelectionDureeComponent,
        OperationModaleComponent,
        DashboardComponent,
        DonneesVibrationComponent,
        DonneesVibrationModaleComponent,
        VibrationDetailPage,
        RecordDetailComponent,
        AssignRecordsModalComponent,
        // AutocompleteComponent,
        RecordModalComponent,
        OperateursComponent,
        OperateursModaleComponent,
        SiteSetupComponent,
        OperationsComponent,
        SiteUsersComponent,
        SiteTemplateSheetConfigComponent,
        RapportCreationModaleComponent,
        SiteControlPointsComponent,
        SiteControlPointsModaleComponent,
        TreeviewComponent,
        TreeviewItemComponent,
        CarteComponent,
        SiteCarteComponent,
        NoeudDetailComponent,
        EpsgModaleComponent,
        SiteKpiComponent,
        AddUsersToSiteModaleComponent,
        SismographControlPointComponent,
        SismographControlPointModalComponent,
        SecurityWindowComponent,
        SiteOrthophotosModaleComponent,
        SiteOrthophotosAddFilesModalComponent,
        SiteDxfAddFileModalComponent,
        RockTypeGuiComponent,
        RockTypeModalComponent,
        AccueilComponent,
        ProfileComponent,

        HibooDashboardComponent,
        DrillPauseReasonModalComponent,
        HibooDashboardBlastComponent,
        HoleChargeViewComponent,
        ImportModalComponent,
        DeliveryModaleComponent,
        DxfManagementComponent,
        PreviewDialogComponent,
        ModifyKpiModalComponent,
        ModifyLoadComponent,
        ModifyHoleComponent,
        TirInfoComponent,
        UserDashboardComponent
    ],
    imports: [
        BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        NgSelectModule,
        DragDropModule,
        SortableModule.forRoot(),
        ModalModule.forRoot(),
        AlertModule.forRoot(),
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatButtonModule,
        MatSlideToggleModule,
        MatTableModule,
        MatSortModule,
        MatIconModule,
        NgxFileDropModule,
        ColorPickerModule,
        NavBarComponent,        // standalone
        BugReportComponent,     // standalone — bouton de signalement, réservé aux sysadmins
        CostComponent,          // standalone
        GeneralComponent,       // standalone
        BlastKpiComponent,      // standalone
        BlastHolesComponent,    // standalone — merged Holes tab (design / loading / 3D / deliveries / drilling)
        FragmentationComponent, // standalone
        BlastEnvironmentComponent, // standalone
        BlastVibrationComponent, // standalone – used in TirDetailComponent
        // Standalone Survey components (used in tir-detail)
        CustomerFeedbackComponent,
        SupplierFeedbackComponent,
        AreaComponent,          // standalone
        
        // Lazy loading wrapper for Three.js 3D viewer
        Blast3DViewerLazyComponent,
        SharedModule,
        NavLeftComponent,
        PageBarComponent,
        EquipmentsComponent,
        ButtonComponent,         // standalone – used in NavBarTopComponent
        AutocompleteComponent,   // standalone – used in RecordsAssignmentComponent
        VibrationChartComponent,   // standalone – used in RecordDetailComponent / BlastVibrationComponent
        RecordsAssignmentComponent, // standalone – used in RecordDetailComponent / BlastVibrationComponent
        FormatTransducerTypePipe,   // standalone – used in VibrationChartComponent

        // Telerik Report Viewer is imported globally via script import
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (createTranslateLoader),
                deps: [HttpClient]
            },
            missingTranslationHandler: { provide: MissingTranslationHandler, useClass: MyMissingTranslationHandler },
            useDefaultLang: true
        }),
        NgxLoadingModule.forRoot({
            animationType: ngxLoadingAnimationTypes.doubleBounce,
            backdropBackgroundColour: 'rgba(0,0,0,0.1)',
            primaryColour: '#7d003f',
            secondaryColour: '#7d003f'
        }),
        AppRoutingModule,
        BsDropdownModule.forRoot(),
        BrowserAnimationsModule,
        ButtonsModule.forRoot(),
        TimepickerModule.forRoot(),
        GraphQLModule,
        NgxDaterangepickerMd.forRoot()
    ],
    providers: [
        CookieService,
        LocalizedDatePipe,
        DecimalPipe,
        provideZonelessChangeDetection(),
        provideNativeDateAdapter(),
        // La locale suit la langue de l'utilisateur (TraductionService la réaligne à chaque
        // changement, cf. changerLangue) : ce sont les libellés du calendrier qui se traduisent.
        // Le champ de saisie, lui, reste en JJ/MM/AAAA quelle que soit la langue.
        { provide: DateAdapter, useClass: EuropeanDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: EUROPEAN_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
        { provide: 'BASE_URL', useFactory: getBaseUrl, deps: [] },

        provideKeycloak({
            config: {
                url: environment.keycloakUrl,
                realm: environment.realm,
                clientId: environment.clientId
            },
            initOptions: {
                onLoad: 'login-required',
                checkLoginIframe: false
            },
            features: [
                withAutoRefreshToken({
                    onInactivityTimeout: 'logout',
                    sessionTimeout: 1200000 // 50 minutes
                })
            ],
            providers: [
                AutoRefreshTokenService,
                UserActivityService,
                {
                    provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
                    useValue: [
                        createInterceptorCondition<IncludeBearerTokenCondition>({
                            urlPattern: new RegExp(
                                `^(?:/api|${environment.exploreURL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}|${environment.vibrationApiURL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}).*$`,
                                'i'
                            ),
                            bearerPrefix: 'Bearer'
                        })
                    ]
                }
            ]
        }),
        provideHttpClient(withInterceptorsFromDi(), withInterceptors([includeBearerTokenInterceptor])),
        { provide: TreeviewEventParser, useClass: DefaultTreeviewEventParser },
        { provide: HTTP_INTERCEPTORS, useClass: CacheBustingInterceptor, multi: true },
        TreeviewConfig,
        { provide: ErrorHandler, useClass: PosthogErrorHandler }
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
